import { intervalToDuration } from 'date-fns';

class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    // Close button
    this.closeButton = this.querySelector('[data-announcement-bar-close]');
    this.closeButton?.addEventListener('click', () => this.closeBar());

    // Copy code button
    this.copyCodeButton = this.querySelector('[data-announcement-bar-copy-code]');
    if (this.copyCodeButton) {
      this.copyCodeButton.addEventListener('click', () => this.copyCode());
      this.toCopyElement = this.copyCodeButton.querySelector('[data-to-copy]');
      this.copiedElement = this.copyCodeButton.querySelector('[data-is-copied]');
    }
  }

  /**
   * Remove the announcement bar from the DOM
   */
  closeBar() {
    this.closest('section')?.remove();
    this.updateCart();
  }

  /**
   * Update the cart with the closed date
   * The attribute is used to update the visibility of the bar from Liquid, avoiding CLS when using JS.
   */
  async updateCart() {
    try {
      await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        ...fetchConfig(),
        body: JSON.stringify({
          attributes: {
            countdown_bar_closed_on: Math.floor(Date.now() / 1000),
          },
        }),
      });
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * Copy text to clipboard, show the 'Copied' text, and reverse the state after 3 seconds
   */
  async copyCode() {
    await navigator.clipboard.writeText(this.toCopyElement.textContent);

    // Clear any existing timeout to prevent multiple timers
    if (this.copyTimeout) clearTimeout(this.copyTimeout);

    // Toggle states immediately
    this.toggleCopyStates(false);

    // Set timeout to reverse the state
    this.copyTimeout = setTimeout(() => {
      this.toggleCopyStates();
    }, 3000);
  }

  /**
   * Toggle the visibility of the copied and to copy elements
   * @param {boolean} setToggle - The state to set the elements to
   */
  toggleCopyStates(setToggle = true) {
    this.copiedElement?.classList.toggle('hidden', setToggle);
    this.toCopyElement?.classList.toggle('hidden', !setToggle);
  }
}

customElements.define('announcement-bar', AnnouncementBar);

class CountdownBar extends HTMLElement {
  connectedCallback() {
    this.end = new Date(this.dataset.endDate);

    this.elements = {
      days: this.querySelector('[data-countdown-days-time]'),
      hours: this.querySelector('[data-countdown-hours-time]'),
      minutes: this.querySelector('[data-countdown-minutes-time]'),
      seconds: this.querySelector('[data-countdown-seconds-time]'),
    };

    this.countdownIntervalId = setInterval(() => {
      this.setCountdown(this.getDuration());
    }, 1000);
  }

  /**
   * Get the duration between the current date and the end date
   * @returns {Object} The duration values
   */
  getDuration() {
    const start = Date.now();
    const {
      years = 0,
      months = 0,
      weeks = 0,
      days = 0,
      hours = 0,
      minutes = 0,
      seconds = 0,
    } = intervalToDuration({ start, end: this.end });

    // Calculate the total number of days based on the years, months, weeks, and days
    const formattedDays = days + weeks * 7 + months * 30 + years * 365;

    return { days: formattedDays, hours, minutes, seconds };
  }

  /**
   * Set the countdown values in the DOM
   * @param {Object} countdown - The countdown values
   * @param {number} countdown.days - The number of days
   * @param {number} countdown.hours - The number of hours
   * @param {number} countdown.minutes - The number of minutes
   * @param {number} countdown.seconds - The number of seconds
   */
  setCountdown({ days, hours, minutes, seconds }) {
    // set all values to 0, if countdown is over
    if (seconds < 0) days = hours = minutes = seconds = 0;

    // set the countdown values in the DOM
    this.elements.days.textContent = String(days).padStart(2, '0');
    this.elements.hours.textContent = String(hours).padStart(2, '0');
    this.elements.minutes.textContent = String(minutes).padStart(2, '0');
    this.elements.seconds.textContent = String(seconds).padStart(2, '0');

    // stop counting on 0
    if (days === 0 && hours === 0 && minutes === 0 && seconds === 0 && this.countdownIntervalId) {
      clearInterval(this.countdownIntervalId);
    }
  }
}

customElements.define('countdown-bar', CountdownBar);
